import React, { JSX, useEffect, useMemo, useState } from 'react';
import { Field, ImageField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Axios from 'axios';
import Icons from 'components/atoms/Icons';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { StyleguideComponentProps } from 'lib/component-props';

type LocationSelecterItem = {
  id: string;
  fields: {
    About: Field<string>;
    AcceptingNewPatients: Field<boolean>;
    AdaComplianceInfo: Field<string>;
    BookOnlineOverrideUrl: Field<string>;
    City: Field<string>;
    Country: Field<string>;
    HomePageUrl: Field<string>;
    HoursOfOperation: Field<string>;
    InsuranceAccepted: Field<string>;
    'Is Permanently Closed': Field<string>;
    KyruusOneId: Field<string>;
    Latitude: Field<number>;
    Longitude: Field<number>;
    Zip: Field<string>;
    Suite: Field<string>;
    Street1: Field<string>;
    State: Field<string>;
    Phone: Field<string>;
    LocationType: Field<string>;
    LocationName: Field<string>;
    LocationId: Field<string>;
    LocationImage: ImageField;
  };
};

type HospitalLocationProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    locationSelector: Array<LocationSelecterItem>;
  };
};

type LatLng = {
  latitude: number;
  longitude: number;
};

const AZURE_MAPS_ENDPOINT = 'https://atlas.microsoft.com';

const getAzureMapsKey = () =>
  process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

const formatAddress = (location: LocationSelecterItem): string => {
  const { Street1, Suite, City, State, Zip } = location.fields;

  return [
    Street1?.value,
    Suite?.value,
    City?.value,
    State?.value,
    Zip?.value,
  ]
    .filter(Boolean)
    .join(', ');
};

const formatDistanceInMiles = (meters?: number): string | null => {
  if (!meters && meters !== 0) return null;

  return `${(meters / 1609.344).toFixed(1)} mi`;
};

const buildAzureStaticMapUrl = (
  location: LocationSelecterItem,
  subscriptionKey?: string
): string => {
  const lat = Number(location.fields.Latitude?.value);
  const lon = Number(location.fields.Longitude?.value);

  if (
    !subscriptionKey ||
    Number.isNaN(lat) ||
    Number.isNaN(lon)
  ) {
    return '';
  }

  const pins = `default|coFF0000||${lon} ${lat}`;

  return `${AZURE_MAPS_ENDPOINT}/map/static?api-version=2024-04-01&style=road&center=${lon},${lat}&zoom=14&width=800&height=600&language=en-US&pins=${encodeURIComponent(
    pins
  )}&subscription-key=${subscriptionKey}`;
};

const buildDirectionsUrl = (
  location: LocationSelecterItem,
  userLocation: LatLng | null
) => {
  const destination = encodeURIComponent(formatAddress(location));

  if (userLocation) {
    return `https://www.google.com/maps/dir/${userLocation.latitude},${userLocation.longitude}/${destination}`;
  }

  return `https://www.google.com/maps/dir//${destination}`;
};

const HospitalLocation = (props: HospitalLocationProps): JSX.Element => {
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const { fields } = props;

  const subscriptionKey = getAzureMapsKey();

  const locations = useMemo(
    () => fields?.locationSelector || [],
    [fields?.locationSelector]
  );

  const [userLocation, setUserLocation] = useState<LatLng | null>(null);
  const [distances, setDistances] = useState<Record<string, string>>({});

  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error('Error getting user location:', error);
        }
      );
    }
  }, []);

  useEffect(() => {
    const fetchDistances = async () => {
      if (!userLocation || !subscriptionKey) return;

      const results: Record<string, string> = {};

      await Promise.all(
        locations.map(async (location) => {
          try {
            const response = await Axios.get(
              `${AZURE_MAPS_ENDPOINT}/route/directions/json`,
              {
                params: {
                  'api-version': '1.0',
                  query: `${userLocation.latitude},${userLocation.longitude}:${location.fields.Latitude.value},${location.fields.Longitude.value}`,
                  travelMode: 'car',
                  routeType: 'fastest',
                  traffic: true,
                  'subscription-key': subscriptionKey,
                },
              }
            );

            const meters =
              response.data?.routes?.[0]?.summary?.lengthInMeters;

            results[location.id] =
              formatDistanceInMiles(meters) ||
              'Distance not available';
          } catch {
            results[location.id] = 'Distance not available';
          }
        })
      );

      setDistances(results);
    };

    fetchDistances();
  }, [userLocation, subscriptionKey, locations]);

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`hospital-location-${props.rendering?.uid}`}
    >
      <div className="jss-inner-container">
        {locations.map((location) => {
          const { id, fields } = location;

          const mapUrl = buildAzureStaticMapUrl(
            location,
            subscriptionKey
          );

          const directionsUrl = buildDirectionsUrl(
            location,
            userLocation
          );

          return (
            <div
              key={id}
              className="hospital-location bg-primary outer-vertical-spacing inner-vertical-spacing"
            >
              <div className="location-details">

                <Text
                  tag="h4"
                  className="header"
                  field={fields.LocationName}
                />

                <Text
                  tag="p"
                  className="address"
                  field={{
                    value: `${fields.Street1?.value} ${fields.Suite?.value}
                    ${fields.City?.value}, ${fields.State?.value} ${fields.Zip?.value}`,
                  }}
                />

                <div className="contact">
                  <Text
                    tag="p"
                    className="m-0"
                    field={fields.Phone}
                  />
                </div>

                <div className="location-section">
                  <span 
                    className="icon icon-location"
                    aria-hidden="true"
                  >
                    <Icons id="location" />
                  </span>

                  {distances[id] && (
                    <>
                      <span>{distances[id]}</span>
                      &nbsp; | &nbsp;
                    </>
                  )}

                  <a href={directionsUrl}>
                    Get Directions
                  </a>
                </div>
              </div>

              <div className="location">
                {mapUrl ? (
                  <a
                    href={directionsUrl}
                    className="map-link"
                  >
                    <img
                      src={mapUrl}
                      className="location-map-image"
                      alt={`Map displaying location of ${fields.LocationName?.value}`}
                    />
                  </a>
                ) : (
                  <div className="location-map-fallback">
                    Map not available
                  </div>
                )}
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HospitalLocation;